import type { ReactElement, ReactNode } from "react";
import { DropdownMenu as DropdownMenuRadix } from "radix-ui";
import { Link as RouterLink, type To } from "react-router";
import type { IconName } from "../../icons";
import { icons } from "../../icons";
import type { IconButtonProps } from "../IconButton/IconButton";
import styles from "./DropdownMenu.css";
import IconButton from "../IconButton/IconButton";
import Tooltip from "../Tooltip/Tooltip";

const Root = DropdownMenuRadix.Root;

/** Wraps whatever trigger the caller passes — it must forward its ref. */
const Trigger = ({ children }: { children: ReactElement }) => (
  <DropdownMenuRadix.Trigger asChild>{children}</DropdownMenuRadix.Trigger>
);

type DropdownMenuContentProps = {
  children: ReactNode;
  /** "wide" fixes the surface at 250px; "auto" grows with its entries. */
  width?: "auto" | "wide";
};

const Content = ({ children, width = "auto" }: DropdownMenuContentProps) => (
  <DropdownMenuRadix.Portal>
    <DropdownMenuRadix.Content
      className={styles.content({ width })}
      align="end"
      sideOffset={4}
    >
      {children}
    </DropdownMenuRadix.Content>
  </DropdownMenuRadix.Portal>
);

type DropdownMenuProps = {
  triggerIcon: IconName;
  triggerAriaLabel: string;
  children: ReactNode;
  triggerButtonVariant?: IconButtonProps["variant"];
  /** Shown on hover/focus of the trigger. */
  triggerTooltip?: string;
};

/**
 * The common case: an icon-only trigger. A caller whose trigger is something
 * else (an Avatar, a labeled button) composes `Root`, `Trigger` and `Content`
 * itself instead.
 */
const DropdownMenu = ({
  triggerIcon,
  triggerAriaLabel,
  triggerButtonVariant = "tertiary",
  triggerTooltip,
  children,
}: DropdownMenuProps) => {
  const triggerButton = (
    <IconButton
      icon={triggerIcon}
      ariaLabel={triggerAriaLabel}
      size="medium"
      color="neutral"
      variant={triggerButtonVariant}
    />
  );

  return (
    <Root>
      <Trigger>
        {triggerTooltip ? (
          <Tooltip content={triggerTooltip}>{triggerButton}</Tooltip>
        ) : (
          triggerButton
        )}
      </Trigger>
      <Content>{children}</Content>
    </Root>
  );
};

type DropdownMenuItemBase = {
  icon?: IconName;
  destructive?: boolean;
  children: string;
};

/**
 * An entry either runs an action (`onSelect`), navigates inside the app
 * (`to`) or leaves it (`href`) — never more than one.
 */
type DropdownMenuItemProps =
  | (DropdownMenuItemBase & {
      onSelect: () => void;
      to?: never;
      href?: never;
      target?: never;
    })
  | (DropdownMenuItemBase & {
      to: To;
      onSelect?: never;
      href?: never;
      target?: never;
    })
  | (DropdownMenuItemBase & {
      href: string;
      target?: "_blank";
      onSelect?: never;
      to?: never;
    });

const Item = (props: DropdownMenuItemProps) => {
  const { icon, destructive, children } = props;
  const Icon = icon ? icons[icon] : null;
  const className = styles.item({ destructive: Boolean(destructive) });
  const content = (
    <>
      {Icon && <Icon size="16px" aria-hidden />}
      {children}
    </>
  );

  // A navigating entry hands its rendering to a link (`asChild`), so it keeps
  // link semantics — middle-click, open in new tab — while Radix still closes
  // the menu on select and drives it from the keyboard.
  if (props.to !== undefined) {
    return (
      <DropdownMenuRadix.Item className={className} asChild>
        <RouterLink to={props.to}>{content}</RouterLink>
      </DropdownMenuRadix.Item>
    );
  }

  if (props.href !== undefined) {
    return (
      <DropdownMenuRadix.Item className={className} asChild>
        <a
          href={props.href}
          target={props.target}
          rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
        >
          {content}
        </a>
      </DropdownMenuRadix.Item>
    );
  }

  return (
    <DropdownMenuRadix.Item className={className} onSelect={props.onSelect}>
      {content}
    </DropdownMenuRadix.Item>
  );
};

type DropdownMenuLabelProps = {
  children: string;
  /** "accent" marks a privileged section, e.g. the admin entries. */
  color?: "neutral" | "accent";
};

/** A caption above a run of entries. Not focusable, not selectable. */
const Label = ({ children, color = "neutral" }: DropdownMenuLabelProps) => (
  <DropdownMenuRadix.Label className={styles.label({ color })}>
    {children}
  </DropdownMenuRadix.Label>
);

/** A rule between groups of entries. */
const Separator = () => (
  <DropdownMenuRadix.Separator className={styles.separator} />
);

DropdownMenu.Root = Root;
DropdownMenu.Trigger = Trigger;
DropdownMenu.Content = Content;
DropdownMenu.Item = Item;
DropdownMenu.Label = Label;
DropdownMenu.Separator = Separator;

export default DropdownMenu;
